// AdminPayouts — Payout review & approval
// Per-payout: approve, reject, hold, view user, view history

import { useState } from 'react';

interface Payout {
  id: string; user: string; userId: string; amount: number; currency: string;
  method: 'ACH' | 'Wire' | 'Crypto' | 'PayPal'; methodDetail: string;
  requested: string; status: 'pending' | 'approved' | 'processing' | 'paid' | 'rejected' | 'on_hold';
  risk: 'low' | 'medium' | 'high'; flags: string[];
}

const PAYOUTS: Payout[] = [
  { id: 'p1', user: 'Brandon Lee',   userId: 'u1', amount: 1240.00, currency: 'USD', method: 'ACH',    methodDetail: 'Chase Bank ••••4521', requested: '2h ago',  status: 'pending',    risk: 'low',    flags: [] },
  { id: 'p2', user: 'Sofia Petrov',  userId: 'u3', amount: 4800.00, currency: 'USD', method: 'Wire',   methodDetail: 'Bank of America ••••8932', requested: '3h ago', status: 'pending',    risk: 'low',    flags: [] },
  { id: 'p3', user: 'Marcus Chen',   userId: 'u2', amount: 12400.00, currency: 'USD', method: 'Wire', methodDetail: 'Wells Fargo ••••1107', requested: '4h ago', status: 'pending',    risk: 'medium', flags: ['First wire request'] },
  { id: 'p4', user: 'Aiden Wright',  userId: 'u4', amount: 2800.00, currency: 'USDC', method: 'Crypto', methodDetail: '0x7C29…84F', requested: '5h ago', status: 'pending',    risk: 'low', flags: [] },
  { id: 'p5', user: 'Aisha Khan',    userId: 'u5', amount: 1800.00, currency: 'USD', method: 'PayPal', methodDetail: 'aisha@email.com', requested: '6h ago', status: 'on_hold', risk: 'high', flags: ['New PayPal account', 'Account < 30 days'] },
  { id: 'p6', user: 'Diego Rivera',  userId: 'u6', amount: 450.00, currency: 'USD', method: 'ACH', methodDetail: 'Chase Bank ••••7821', requested: '1d ago', status: 'pending', risk: 'low', flags: [] },
  { id: 'p7', user: 'Yuki Tanaka',   userId: 'u7', amount: 3200.00, currency: 'USD', method: 'ACH', methodDetail: 'MUFG Bank ••••2231', requested: '1d ago', status: 'processing', risk: 'low', flags: [] },
];

const RISK_META = { low: { color: '#34C759', label: 'Low' }, medium: { color: '#FF9500', label: 'Med' }, high: { color: '#FF3B30', label: 'High' } };
const STATUS_META = {
  pending:    { color: '#FF9500', label: 'Pending' },
  approved:   { color: '#34C759', label: 'Approved' },
  processing: { color: '#007AFF', label: 'Processing' },
  paid:       { color: '#34C759', label: 'Paid' },
  rejected:   { color: '#FF3B30', label: 'Rejected' },
  on_hold:    { color: '#FF3B30', label: 'On hold' },
};

export function AdminPayouts() {
  const [payouts, setPayouts] = useState(PAYOUTS);
  const [filter, setFilter] = useState<'all' | 'pending' | 'high_risk' | 'on_hold'>('all');
  const [selected, setSelected] = useState<string[]>([]);

  const filtered = payouts.filter((p) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return p.status === 'pending';
    if (filter === 'high_risk') return p.risk === 'high' || p.risk === 'medium';
    if (filter === 'on_hold') return p.status === 'on_hold';
    return true;
  });

  function approve(id: string) {
    if (!confirm('Approve this payout?')) return;
    setPayouts((curr) => curr.map((p) => p.id === id ? { ...p, status: 'processing' } : p));
    alert('Payout approved. Now processing via Stripe.');
  }
  function reject(id: string) {
    if (!confirm('Reject this payout?')) return;
    setPayouts((curr) => curr.map((p) => p.id === id ? { ...p, status: 'rejected' } : p));
    alert('Payout rejected. User notified with reason.');
  }
  function hold(id: string) {
    if (!confirm('Put this payout on hold for review?')) return;
    setPayouts((curr) => curr.map((p) => p.id === id ? { ...p, status: 'on_hold' } : p));
    alert('Payout on hold. Compliance team notified.');
  }

  function bulk(action: string) {
    if (selected.length === 0) { alert('Select payouts first'); return; }
    if (!confirm(`${action} ${selected.length} payout(s)?`)) return;
    setPayouts((curr) => curr.map((p) => {
      if (!selected.includes(p.id)) return p;
      if (action === 'Approve all') return { ...p, status: 'processing' };
      if (action === 'Hold all') return { ...p, status: 'on_hold' };
      return p;
    }));
    alert(`${action} applied to ${selected.length} payouts`);
    setSelected([]);
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">Payouts</h1>
          <p className="text-[12px] text-white/50">{filtered.length} shown · {payouts.filter((p) => p.status === 'pending').length} pending approval</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => bulk('Approve all')} data-testid="bulk-approve" className="h-9 rounded-[8px] bg-[#34C759] px-3 text-[12px] font-semibold text-white active:opacity-40">Approve all</button>
          <button onClick={() => bulk('Hold all')} data-testid="bulk-hold" className="h-9 rounded-[8px] bg-[#FF9500] px-3 text-[12px] font-semibold text-white active:opacity-40">Hold all</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5">
        {(['all', 'pending', 'high_risk', 'on_hold'] as const).map((f) => (
          <button
            key={f} onClick={() => setFilter(f)}
            data-testid={`payfilter-${f}`}
            className={['rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'
            ].join(' ')}
          >
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Bulk bar */}
      {selected.length > 0 && (
        <div className="rounded-[12px] bg-[#FF9500]/10 border border-[#FF9500]/30 px-3 py-2 flex items-center gap-2">
          <div className="text-[12px] font-semibold text-[#FF9500]">{selected.length} selected</div>
          <div className="flex-1" />
          <button onClick={() => setSelected([])} className="text-[11px] text-white/60">Clear</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Pending total"   value={`$${payouts.filter((p) => p.status === 'pending').reduce((s, p) => s + p.amount, 0).toLocaleString()}`} color="#FF9500" />
        <Kpi label="High risk"       value={payouts.filter((p) => p.risk === 'high').length.toString()} color="#FF3B30" />
        <Kpi label="Processing"      value={payouts.filter((p) => p.status === 'processing').length.toString()} color="#007AFF" />
        <Kpi label="Paid today"      value="$8,420" color="#34C759" />
      </div>

      {/* Payouts list */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 bg-white/[0.02] text-[10px] font-bold uppercase text-white/40">
          <div className="col-span-1"><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={(e) => setSelected(e.target.checked ? filtered.map((p) => p.id) : [])} data-testid="pay-select-all" className="accent-[#FF3B30]" /></div>
          <div className="col-span-2">User</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-2">Method</div>
          <div className="col-span-1">Risk</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Requested</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {filtered.map((p) => {
          const r = RISK_META[p.risk];
          const s = STATUS_META[p.status];
          return (
            <div key={p.id} data-testid={`payout-${p.id}`} className={['grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 last:border-b-0 items-center text-[11px]', p.risk === 'high' ? 'bg-[#FF3B30]/[0.04]' : ''].join(' ')}>
              <div className="col-span-1"><input type="checkbox" checked={selected.includes(p.id)} onChange={(e) => setSelected((curr) => e.target.checked ? [...curr, p.id] : curr.filter((x) => x !== p.id))} data-testid={`pay-select-${p.id}`} className="accent-[#FF3B30]" /></div>
              <div className="col-span-2">
                <div className="text-white font-semibold">{p.user}</div>
                <div className="text-[10px] text-white/40">{p.userId}</div>
              </div>
              <div className="col-span-1 text-right text-white font-bold tabular-nums">${p.amount.toFixed(0)}<div className="text-[9px] text-white/40 font-normal">{p.currency}</div></div>
              <div className="col-span-2 text-white/80">{p.method}<div className="text-[10px] text-white/40">{p.methodDetail}</div></div>
              <div className="col-span-1"><span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: r.color + '20', color: r.color }}>{r.label}</span></div>
              <div className="col-span-1"><span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: s.color + '20', color: s.color }}>{s.label}</span></div>
              <div className="col-span-1 text-white/60 text-[10px]">{p.requested}</div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                {p.flags.length > 0 && <span className="text-[10px] text-[#FF3B30]">🚩</span>}
                <button onClick={() => approve(p.id)} data-testid={`pay-approve-${p.id}`} className="h-7 rounded-full bg-[#34C759] px-2 text-[10px] font-semibold text-white active:opacity-40">Approve</button>
                <button onClick={() => hold(p.id)} data-testid={`pay-hold-${p.id}`} className="h-7 rounded-full bg-[#FF9500] px-2 text-[10px] font-semibold text-white active:opacity-40">Hold</button>
                <button onClick={() => reject(p.id)} data-testid={`pay-reject-${p.id}`} className="h-7 rounded-full bg-[#FF3B30] px-2 text-[10px] font-semibold text-white active:opacity-40">Reject</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-[12px] bg-[#1C1C1E] border border-white/5 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="mt-1 text-[18px] font-bold tabular-nums" style={{ color: color || '#FFFFFF' }}>{value}</div>
    </div>
  );
}
